import { useApolloClient, useMutation } from '@apollo/client';
import { AppRoutePath } from 'config/constants';
import { useAsyncGlobalAction } from 'hooks/useGlobalAction';
import Cookie from 'js-cookie';
import analytics from 'modules/analytics';
import { workspace } from 'modules/analytics/constants';
import { removeTypeName } from 'modules/analytics/util';
import { priorClient } from 'modules/Apollo/priorClient';
import {
  dicomTransfersState,
  dicomTransferJobsState,
  isDicomTransfersDismissedState,
} from 'domains/worklist/Worklist/DicomSend/state';
import {
  AUTH_WITH_EXT_TOKEN,
  AUTHENTICATE,
  AUTHENTICATE_MFA,
  GET_ME,
  UNAUTHENTICATE,
} from 'modules/Apollo/queries';
import { logger } from 'modules/logger';
import { useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import { isRefreshTokenProvided, logout } from 'utils/token';
import { createContext } from '../common/ui/util/createContext';
import {
  AuthWithExtTokenMutation,
  AuthWithExtTokenMutationVariables,
  AuthenticateMfaMutation,
  AuthenticateMfaMutationVariables,
  AuthenticateMutation,
  AuthenticateMutationVariables,
  GetMeFragmentFragment,
  UnauthenticateMutation,
  UnauthenticateMutationVariables,
  User,
} from '../generated/graphql';
import { useLiveWorkerGeneration } from 'domains/viewer/Viewer/EscapeHatch/state';
import {
  createPixelDataSharedWorker,
  PixelDataSharedWorker,
} from '../modules/viewer/workers/PixelDataSharedWorker';
import { clearStoredData } from './useClearStoredDataOnClose';
import { useSetUserPermissions } from './useGetUserPermissions';
import { CurrentUserQueryContext } from './useCurrentUser/CurrentUserQueryContext';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import useInterval from 'react-use/esm/useInterval';
import { CancelTransferScope } from 'modules/viewer/workers/pixelDataSharedWorkerTypes';
import { WithApolloContext } from 'modules/Apollo/types';

export type Auth = {
  isAuthenticated: boolean;
  error: Nullable<string>; // unknown, LoginComponent is untyped but it only checks if error is not null,
  login: (arg: { email: string; password: string }) => Promise<void>;
  loginMFA: (arg: { email: string; totp: string; mfaSessionId: string }) => Promise<void>;
  authWithExternalToken: (token: string) => Promise<{ caseSmid: string }>;
  logout: () => Promise<void>;
};

export const [AuthProvider, useAuthContext, AuthContext] = createContext<
  Omit<Auth, 'authWithExternalToken'>
>({
  strict: true,
  errorMessage:
    'useAuth: `context` is undefined. Seems you forgot to wrap component within the AuthProvider',
  name: 'AuthContext',
});

export const authState = atom({
  key: 'auth',
  default: isRefreshTokenProvided(),
});

const isAuthedAsExternalUser = atom({
  key: 'ext.auth',
  default: false,
});

/**
 * Whether the server has rejected the session the auth cookie claims we have.
 *
 * `authState` defaults from the `refresh_token_provided` cookie, which is the only auth cookie the
 * frontend can read. That cookie says a refresh token was issued, not that the server still honors
 * it, so it stays true after overnight expiry, server-side revocation or a password change. getMe
 * is the first thing to learn the truth, so its error is what the auth state has to defer to.
 *
 * This is deliberately render-synchronous rather than an effect that clears `authState`: every
 * consumer below has to see the same answer in the same render. A single render in which routing
 * still believes it is authenticated is enough to mount the authenticated shell against a rejected
 * session, and the components it mounts rethrow the getMe error from above every ErrorBoundary,
 * which unmounts the React root and leaves an empty `#root` (VX-5162).
 *
 * Read through `useContext` rather than `useCurrentUserQueryContext` because not every caller of
 * `useIsAuthenticated` renders under `CurrentUserProvider` (Storybook, isolated tests), and without
 * a getMe result there is nothing to disagree with the cookie.
 */
const useIsSessionRejected = (): boolean =>
  useContext(CurrentUserQueryContext)?.query.error != null;

export const useIsAuthenticated = (): boolean => {
  const hasAuthCookie = useRecoilValue(authState);
  const isSessionRejected = useIsSessionRejected();

  return hasAuthCookie && !isSessionRejected;
};

export const useIsAuthenticatedAsExternalUser = (): boolean =>
  useRecoilValue(isAuthedAsExternalUser);

/**
 * When a user logs out of one page, the access and refresh token cookies are removed for the origin on all pages.
 * Additionally, a `refresh_token_provided` cookie is set to false, which is what the auth code checks for.
 * Our typical logout flow propagates an event to other pages when a user logs out, which triggers the recoil state updates.
 * However, this event might get lost if the page is hibernating, so this hook will fire and force the unauthenticated state
 * after the page wakes up. Even if the page was hibernating, the cookie changes on one page will still apply to all others
 * in the app.sironamedical.com origin.
 */
export function useMonitorAuthToken() {
  const [isAuthenticated, setAuthenticated] = useRecoilState(authState);
  const resetDicomTransfers = useResetRecoilState(dicomTransfersState);
  const resetDicomTransferJobs = useResetRecoilState(dicomTransferJobsState);
  const resetDicomTransfersDismissed = useResetRecoilState(isDicomTransfersDismissedState);

  const checkToken = useCallback(() => {
    const tokenProvided = isRefreshTokenProvided();

    if (tokenProvided !== isAuthenticated) {
      // This path skips handleLogout, so clear the session's transfers here too. Otherwise the tab
      // holds the previous user's exams and a later login on it inherits them.
      if (!tokenProvided) {
        resetDicomTransfers();
        resetDicomTransferJobs();
        resetDicomTransfersDismissed();
      }
      setAuthenticated(tokenProvided);
    }
  }, [
    isAuthenticated,
    setAuthenticated,
    resetDicomTransfers,
    resetDicomTransferJobs,
    resetDicomTransfersDismissed,
  ]);

  useInterval(checkToken, 1000);
}

/**
 * Identifies a user for analytics after login
 */
function identifyUser(authenticateResponse?: GetMeFragmentFragment | null) {
  if (authenticateResponse == null) {
    logger.warn('Empty authentication response when attempting to identify user');
    return;
  }
  const { id, firstName, lastName, email, clinic, reporterSettings } = authenticateResponse;
  const additionalData = {
    segmentData: {
      reporterSettings: removeTypeName(reporterSettings),
    },
  } as const;
  analytics.identify(
    {
      id,
      firstName,
      lastName,
      email,
      clinic: {
        name: clinic?.name || '',
        smid: clinic?.smid || '',
      },
    },
    additionalData
  );

  analytics.track(workspace.usr.login, {
    email,
    clinic: {
      name: clinic?.name ?? '',
      smid: clinic?.smid ?? '',
    },
  });
}

export enum AuthError {
  TOKEN_EXPIRED = 'Token expired',
  TOKEN_INVALID = 'Invalid authentication response',
}

/**
 * Hook for handling all authentication-related operations, which includes: login (with or without
 * MFA), token access, and log out.
 */
const useAuth = ({ context }: WithApolloContext): Auth => {
  const [isAuthenticated, setIsAuthenticated] = useRecoilState(authState);
  // BackgroundRoute reads `isAuthenticated` from here to decide whether to redirect off an authless
  // route and whether to mount the authenticated shell, so it needs the same answer
  // `useIsAuthenticated` gives. The atom itself stays cookie-shaped; only what callers see is gated.
  const isSessionRejected = useIsSessionRejected();
  const [error, setError] = useState(null);
  const setIsExternalUser = useSetRecoilState(isAuthedAsExternalUser);
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticate] = useMutation<AuthenticateMutation, AuthenticateMutationVariables>(
    AUTHENTICATE,
    {
      context,
    }
  );
  const [authenticateMFA] = useMutation<AuthenticateMfaMutation, AuthenticateMfaMutationVariables>(
    AUTHENTICATE_MFA,
    { context }
  );
  const [unauthenticate] = useMutation<UnauthenticateMutation, UnauthenticateMutationVariables>(
    UNAUTHENTICATE,
    { context }
  );
  const [authWithExtToken] = useMutation<
    AuthWithExtTokenMutation,
    AuthWithExtTokenMutationVariables
  >(AUTH_WITH_EXT_TOKEN, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (data?.authWithExtToken?.user?.type === 'ExternalIntegrationUser') {
        setIsExternalUser(true);
      }
    },
    onError: (error) => {
      logger.error('Error authenticating with token', { error });
      throw new Error(AuthError.TOKEN_EXPIRED);
    },
    context: {
      ...context,
      telemetry: { ...context.telemetry, fetchPolicy: 'no-cache' },
    },
  });
  // treat loading as false
  // do not use the Suspense version of Split, as that will suspend until a user is available
  // this will not always be the case during logout scenarios
  const [useDedupedPixelWorker] = useFeatureFlagEnabled(FF.PIXEL_WORKER_DEDUPLICATION);
  const pixelWorkerGeneration = useLiveWorkerGeneration();
  const resetDicomTransfers = useResetRecoilState(dicomTransfersState);
  const resetDicomTransferJobs = useResetRecoilState(dicomTransferJobsState);
  const resetDicomTransfersDismissed = useResetRecoilState(isDicomTransfersDismissedState);

  const handleLogout = useCallback(async () => {
    let worker: PixelDataSharedWorker | null = null;
    try {
      // Cancel before unauthenticating: frames still loading would otherwise keep requesting signed
      // URLs against the destroyed session, each one failing with UNAUTHENTICATED.
      // VX-4363: connect at the live generation so cancel-transfer/clear-cache reach the worker
      // that actually holds this session's PHI-bearing pixel cache, not the bare gen-0 default.
      worker = createPixelDataSharedWorker(
        useDedupedPixelWorker,
        undefined,
        false,
        pixelWorkerGeneration
      );
      worker.port.postMessage({
        type: 'cancel-transfer',
        scope: CancelTransferScope.All,
      });
    } catch (error) {
      logger.error('Cancelling pixel transfers on logout failed:', error);
    }

    try {
      await unauthenticate({
        // Suppress error when unauthenticating without a valid user, such as when a user's refresh token expires.
        onError: () => {},
      });
    } catch (error) {
      logger.error('Unauthenticate failed:', error);
    }

    try {
      client.stop();
      // Priors are fetched through a separate Apollo client/cache (`priorClient`).
      // Stop + clear it on logout too, or a re-login in the same SPA session can
      // serve cached prior-study data (PHI) without a fresh network/auth check.
      priorClient.stop();

      Cookie.set('refresh_token_provided', 'false');
      analytics.reset();

      await clearStoredData(worker);

      setError(null);
      logout(); // Clears local and session storage of not-persisted entries
    } catch (error) {
      logger.error('Logout and cleanup failed:', error);
    } finally {
      // Clears the session's DICOM sends from both the Recoil store, which survives an in-place
      // logout (session expiry, inactivity), and sessionStorage. In `finally` because the cleanup
      // above can throw, and a failed teardown must not leave the previous user's transfers behind.
      resetDicomTransfers();
      resetDicomTransferJobs();
      resetDicomTransfersDismissed();

      const [mainResult, priorResult] = await Promise.allSettled([
        Promise.resolve().then(() => client.clearStore()),
        Promise.resolve().then(() => priorClient.clearStore()),
      ]);
      if (mainResult.status === 'rejected') {
        logger.error('Failed to clear main Apollo cache on logout:', mainResult.reason);
      }
      if (priorResult.status === 'rejected') {
        logger.error('Failed to clear prior-study Apollo cache on logout:', priorResult.reason);
      }
      setIsAuthenticated(false);
    }
  }, [
    client,
    setIsAuthenticated,
    unauthenticate,
    useDedupedPixelWorker,
    pixelWorkerGeneration,
    resetDicomTransfers,
    resetDicomTransferJobs,
    resetDicomTransfersDismissed,
  ]);

  const handleGlobalLogout = useAsyncGlobalAction('use-auth-logout', handleLogout, {
    callSelf: true,
  });

  const handleAuthenticate = useCallback(() => {
    setIsAuthenticated(true);
  }, [setIsAuthenticated]);

  const setUserPermissions = useSetUserPermissions();

  /** Callback for common code to execute upon a successful authorization */
  const _postAuthHandler = useCallback(
    async (user: GetMeFragmentFragment) => {
      logger.debug('Clearing localforage cache on login');
      const worker = createPixelDataSharedWorker();
      // Clear localforage cache on login so that we don't keep stale data and we allow users to
      // login with a different account and start from scratch
      await clearStoredData(worker);

      // Same reasoning for the session's DICOM sends, which outlive a tab whose token simply
      // expired: that tab never runs handleLogout, so without this the next login inherits the
      // previous user's transfers from sessionStorage.
      resetDicomTransfers();
      resetDicomTransferJobs();
      resetDicomTransfersDismissed();

      // Set the refresh token's expiration
      const expires = new Date();
      expires.setDate(expires.getDate() + 30); // Hardcoded because the token doesn't provide one
      Cookie.set('refresh_token_provided', 'true', { expires });

      // The centralized getMe owner mounts before login. Refresh it after the auth cookie is set so
      // same-page logins receive user data before authenticated consumers begin rendering.
      await client.refetchQueries({
        include: [GET_ME],
      });

      // Clear auth error state
      setError(null);
      handleAuthenticate();
      // Identify the user for analytics
      identifyUser(user);
    },
    [
      client,
      handleAuthenticate,
      resetDicomTransfers,
      resetDicomTransferJobs,
      resetDicomTransfersDismissed,
    ]
  );

  const handleLogin = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      setError(null);
      try {
        const response = await authenticate({ variables: { email, password } });

        const authenticateResponse = response.data?.authenticate;

        if (!authenticateResponse) {
          throw new Error(AuthError.TOKEN_INVALID);
        }

        if (authenticateResponse.__typename === 'MFARequired') {
          const mfaSessionId = authenticateResponse?.mfaSessionId;
          navigate(AppRoutePath.VERIFY_MFA, {
            state: {
              mfa_session: mfaSessionId,
              email,
            },
          });
        } else {
          if (authenticateResponse.__typename !== 'User') return;
          // Extract permissions from user object to avoid storing them in GraphQL cache
          const { permissions, ...user } = authenticateResponse;
          setUserPermissions(permissions);

          // TODO: The typecast to user is not ideal here; we should find a way to avoid it. But
          // it's a lesser of two evils to explicitly typecast than to have `user` defined as any
          // due to an untyped useMutation.
          await _postAuthHandler(user as User);

          const from = location.state?.from;
          navigate(from ?? { pathname: AppRoutePath.WORKLIST });
        }
      } catch (err: any) {
        analytics.error(err, { email });
        setError(err);
        return;
      }
    },
    [authenticate, setUserPermissions, navigate, location.state, _postAuthHandler]
  );

  const handleMFALogin = useCallback(
    async ({
      email,
      totp,
      mfaSessionId,
    }: {
      email: string;
      totp: string;
      mfaSessionId: string;
    }) => {
      setError(null);
      try {
        const response = await authenticateMFA({
          variables: { email, totp, mfaSessionId },
        });

        const user = response.data?.authenticateMFA;
        if (user != null) {
          setUserPermissions(user.permissions);

          await _postAuthHandler(user);
        }

        const from = location.state?.from;
        navigate(from ?? { pathname: AppRoutePath.WORKLIST });
      } catch (err: any) {
        analytics.error(err, { email });
        setError(err);
        return;
      }
    },
    [authenticateMFA, _postAuthHandler, location.state, navigate, setUserPermissions]
  );

  const handleAuthWithExtToken = useCallback(
    async (token: string) => {
      let data;
      try {
        const resp = await authWithExtToken({ variables: { token } });
        data = resp.data?.authWithExtToken;
      } catch (ex) {
        throw new Error('Invalid authentication response');
      } finally {
        if (!data) {
          // eslint-disable-next-line no-unsafe-finally
          throw new Error('Invalid authentication response');
        }
      }
      const { caseSmid, user } = data;
      const permissions = user?.permissions ?? {};
      setUserPermissions(permissions);
      await _postAuthHandler(user as User);

      return { caseSmid: caseSmid as string };
    },
    [_postAuthHandler, authWithExtToken, setUserPermissions]
  );

  return {
    isAuthenticated: isAuthenticated && !isSessionRejected,
    logout: handleGlobalLogout,
    login: handleLogin,
    loginMFA: handleMFALogin,
    authWithExternalToken: handleAuthWithExtToken,
    error,
  };
};

export default useAuth;
